import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Period, PERIODS_LIST_KEY } from 'src/app/pages/periods/service';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

/**
 * Global sezon seçimi ("sezonlar arasında geçiş").
 *
 * - Varsayılan: aktif sezon.
 * - "Tüm Veriler" seçildiğinde `periodId` null olur ve listeler filtresiz çalışır.
 * - Seçim, firma bazında localStorage'da saklanır: `selectedPeriodId:<company_id>`
 *   Değerler: `all` (tüm veriler) | `<id>` (belirli sezon). Kayıt yoksa aktif sezon izlenir.
 */

type StoredSelection = 'auto' | 'all' | number;

export interface SelectedPeriodContextValue {
  /** Seçili sezonun id'si; "Tüm Veriler" seçiliyse null. */
  periodId: number | null;
  /** Seçili sezon kaydı; "Tüm Veriler" seçiliyse null. */
  period: Period | null;
  /** Firmaya ait tüm sezonlar. */
  periods: Period[];
  /** Aktif sezon (varsa). */
  activePeriod: Period | null;
  /** `null` → Tüm Veriler. Aktif sezon seçilirse seçim aktif sezonu izlemeye devam eder. */
  setPeriodId: (id: number | null) => void;
  isLoading: boolean;
}

const defaultValue: SelectedPeriodContextValue = {
  periodId: null,
  period: null,
  periods: [],
  activePeriod: null,
  setPeriodId: () => {},
  isLoading: false,
};

const SelectedPeriodContext = createContext<SelectedPeriodContextValue>(defaultValue);

const storageKey = (companyId: number | string) => `selectedPeriodId:${companyId}`;

const readStored = (companyId?: number | string | null): StoredSelection => {
  if (!companyId) return 'auto';
  try {
    const raw = window.localStorage.getItem(storageKey(companyId));
    if (!raw) return 'auto';
    if (raw === 'all') return 'all';
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 'auto';
  } catch {
    return 'auto';
  }
};

const writeStored = (companyId: number | string | null | undefined, value: StoredSelection) => {
  if (!companyId) return;
  try {
    if (value === 'auto') window.localStorage.removeItem(storageKey(companyId));
    else window.localStorage.setItem(storageKey(companyId), String(value));
  } catch {
    /* localStorage kullanılamıyor olabilir; sessizce geç */
  }
};

export const SelectedPeriodProvider: React.FC = ({ children }) => {
  const { user } = useAppSelector((state) => state.auth);
  const companyId: number | undefined = user?.company_id;

  const { data, isLoading } = useQuery<Period[]>(
    [PERIODS_LIST_KEY],
    async () => {
      const res = await axios.get<Period[]>(`${REACT_APP_API_URL}/periods/list`, { params: { company_id: companyId, user_id: user?.id } });
      return Array.isArray(res?.data) ? res.data : [];
    },
    {
      enabled: !!companyId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
  );

  const periods: Period[] = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const activePeriod = useMemo(() => periods.find((p) => p.is_active) || null, [periods]);

  const [selection, setSelection] = useState<StoredSelection>(() => readStored(companyId));

  // Şube/firma değişince o firmaya ait kayıtlı seçimi yükle
  useEffect(() => {
    setSelection(readStored(companyId));
  }, [companyId]);

  const period = useMemo<Period | null>(() => {
    if (selection === 'all') return null;
    if (selection === 'auto') return activePeriod;
    return periods.find((p) => p.id === selection) || activePeriod;
  }, [selection, periods, activePeriod]);

  const setPeriodId = useCallback(
    (id: number | null) => {
      let next: StoredSelection;
      if (id === null) next = 'all';
      else if (activePeriod && activePeriod.id === id) next = 'auto';
      else next = id;
      setSelection(next);
      writeStored(companyId, next);
    },
    [companyId, activePeriod],
  );

  const value = useMemo<SelectedPeriodContextValue>(
    () => ({
      periodId: period?.id ?? null,
      period,
      periods,
      activePeriod,
      setPeriodId,
      isLoading: !!companyId && isLoading,
    }),
    [period, periods, activePeriod, setPeriodId, isLoading, companyId],
  );

  return <SelectedPeriodContext.Provider value={value}>{children}</SelectedPeriodContext.Provider>;
};

export const useSelectedPeriod = () => useContext(SelectedPeriodContext);
